import type { editor } from "monaco-editor";

import * as React from "react";

import Editor, { useMonaco } from "@monaco-editor/react";
import DeprecatedLabeledControl from "@v1/components/common/designSystem/DeprecatedLabeledControl";
import InlineAlert from "@v1/components/designSystem/alert/InlineAlert";
import { useElementEditorDataContext } from "@v1/contexts/ElementEditorDataContext";
import { useElementEditorErrorContext } from "@v1/contexts/ElementEditorErrorContext";
import debounce from "lodash-es/debounce";

const ElementCustomCssEditor: React.FC = () => {
  const {
    errorMapping,
    setErrors: setElementErrors,
    clearErrors,
  } = useElementEditorErrorContext();
  const { element, onChangeElement } = useElementEditorDataContext();

  const [cssErrors, setCssErrors] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (cssErrors.length > 0) {
      setElementErrors("customCss", ["invalidCustomCss"]);
    } else if (errorMapping["customCss"]?.includes("invalidCustomCss")) {
      clearErrors("customCss", ["invalidCustomCss"]);
    }
  }, [cssErrors, clearErrors, setElementErrors, errorMapping]);

  const monaco = useMonaco();
  monaco?.editor.defineTheme("replo", {
    base: "vs",
    colors: {
      "editor.background": "#f5f5f5", // neutral-100
      "editor.lineHighlightBackground": "#f1f5f9",
    },
    inherit: true,
    rules: [],
  });

  const handleCSSContentChange = (value: string) => {
    onChangeElement((element) => ({
      ...element,
      customCss: value,
    }));
  };

  const handleErrors = debounce((errors: editor.IMarker[]) => {
    setCssErrors(
      errors.map(({ endLineNumber, endColumn, message }) => {
        return `error on line ${endLineNumber} at column ${endColumn}: ${message}`;
      }),
    );
  }, 300);

  return (
    <DeprecatedLabeledControl
      label="Custom CSS"
      id="element-data-css"
      size="sm"
    >
      <p className="text-muted text-xs">
        This CSS will be inserted along with all other styles generated for your
        Replo content.
      </p>
      <div
        className="flex flex-col gap-2 overflow-hidden rounded-sm"
        data-testid="custom-css-editor"
      >
        <Editor
          height="200px"
          defaultLanguage="css"
          defaultValue={element.customCss ?? ""}
          onValidate={handleErrors}
          onChange={(value?: string) => handleCSSContentChange(String(value))}
          options={{
            minimap: { enabled: false },
            // NOTE (Noah, 2026-02-15, REPL-23631): Disable occurrence highlighting
            // because Monaco internally constructs a regex from the text under
            // the cursor, which crashes on content with regex metacharacters
            // (e.g. CSS `* { margin: 0; }` inside a <style> tag).
            occurrencesHighlight: false,
            scrollbar: { verticalScrollbarSize: 0 },
            lineNumbers: "off",
            lineDecorationsWidth: 0,
            padding: { top: 16 },
          }}
          theme="replo"
        />
      </div>
      {element.customCss && cssErrors.length > 0 && (
        <InlineAlert variant="error" multiline>
          <div className="flex flex-col gap-2">
            <p>
              Replo detected CSS which is invalid. This may cause your CSS to
              not be saved. If you see this page not working correctly, please
              reach out to support@replo.app with any questions.
            </p>
            <ul className="flex flex-col gap-1">
              {cssErrors.map((error) => (
                <li key={error} style={{ listStyleType: "circle" }}>
                  {error}
                </li>
              ))}
            </ul>
          </div>
        </InlineAlert>
      )}
    </DeprecatedLabeledControl>
  );
};

export default ElementCustomCssEditor;
